/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';

import { getAuthCookie, loaderClient } from '@store/index';
import { axiosErrorMiddleware } from '../common';
import { QueryFunctionContext } from '@tanstack/react-query';
import { INTERNAL_LOADER_URL } from '@common/hooks/useDocument';
import {
  CheckDeleteFileParams,
  ContentfulLoaderCollection,
  CreateSlackLoaderParams,
  CreateSlackLoaderResponse,
  DeleteLoaderParams,
  GetCollectionsParams,
  GetContentfulCollectionsParams,
  GetLoaderParams,
  GuruLoaderCollection,
  IntercomLoaderCollection,
  LoaderConfiguration,
  RefreshLoaderParams,
  SlackLoaderChannel,
  StoreLoaderParams,
  UpdateLoaderScheduleParams,
} from './loaders.types';

export const getIntercomCollections = async ({ queryKey }: QueryFunctionContext<[string, GetCollectionsParams]>) => {
  const [, { secret_identifier, service_id }] = queryKey;

  const url = `/${service_id}/loaders/intercom/collections?secret_identifier=${secret_identifier}`;
  const response = await loaderClient.get<IntercomLoaderCollection[]>(url);
  return response.data;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getSlackChannels = async ({ queryKey }: QueryFunctionContext<[string, GetCollectionsParams]>) => {
  const [, { secret_identifier, service_id, user_id, org_id }] = queryKey;

  const url = `${INTERNAL_LOADER_URL}/${service_id}/loaders/slack/channels?secret_identifier=${secret_identifier}`;
  const internalAuthHeaders = {
    Authorization: `Bearer ${getAuthCookie()}`,
    'telnyx-ratelimit-user-id': user_id,
    'telnyx-ratelimit-org-id': org_id,
    'Content-Type': 'application/json',
  };

  const response = await axios.get<SlackLoaderChannel[]>(url, { headers: internalAuthHeaders });
  return response.data;
};

export const getContentfulContentModels = async ({
  queryKey,
}: QueryFunctionContext<[string, GetContentfulCollectionsParams]>) => {
  const [, { secret_identifier, service_id, space }] = queryKey;

  const url = `/${service_id}/loaders/contentful/content_models?secret_identifier=${secret_identifier}&space=${space}`;
  const response = await loaderClient.get<ContentfulLoaderCollection[]>(url);
  return response.data;
};

export const getGuruCollections = async ({ queryKey }: QueryFunctionContext<[string, GetCollectionsParams]>) => {
  const [, { secret_identifier, service_id }] = queryKey;

  const url = `/${service_id}/loaders/guru/collections?secret_identifier=${secret_identifier}`;
  const response = await loaderClient.get<GuruLoaderCollection[]>(url);
  return response.data;
};

export const getLoader = async ({ queryKey }: QueryFunctionContext<[string, string, GetLoaderParams]>) => {
  const [, , { service_id, bucket_name }] = queryKey;

  const url = `/${service_id}/loaders/${bucket_name}`;
  const response = await loaderClient.get<LoaderConfiguration>(url);
  return response?.data;
};

export const storeLoader = async ({ service_id, loader }: StoreLoaderParams) => {
  try {
    const url = `/${service_id}/loaders`;
    const response = await loaderClient.post(url, { loader });

    if (response?.data) return response?.data;
    return [];
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};

export const refreshLoader = async ({ service_id, bucket_name }: RefreshLoaderParams) => {
  try {
    const url = `/${service_id}/loaders/${bucket_name}/refresh`;
    const response = await loaderClient.patch(url);

    if (response?.data?.error) throw new Error(response?.data?.error);
    if (!response?.data?.success) throw new Error('An unexpected error occured.');

    return { success: true, error: null };
  } catch (error: any | unknown) {
    return axiosErrorMiddleware(error);
  }
};

export const checkDeletedFiles = async ({ service_id, bucket_name, region, files }: CheckDeleteFileParams) => {
  try {
    const url = `/${service_id}/loaders/${bucket_name}/${region}/validate`;
    const response = await loaderClient.post(url, { files });

    if (response?.data?.error) throw new Error(response?.data?.error);
    if (!response?.data?.success) throw new Error('An unexpected error occured.');

    return { success: true, error: null };
  } catch (error: any | unknown) {
    return axiosErrorMiddleware(error);
  }
};

export const updateLoaderSchedule = async ({
  service_id,
  bucket_name,
  intervalValue,
  unit,
  user_id,
}: UpdateLoaderScheduleParams) => {
  try {
    const url = `/${service_id}/loaders/schedule?bucket_name=${bucket_name}&user_id=${user_id}`;
    const response = await loaderClient.patch(url, {
      refresh_interval: intervalValue,
      refresh_interval_config: JSON.stringify({ value: intervalValue, unit }),
    });

    if (response?.data?.error) throw new Error(response?.data?.error);
    if (!response?.data?.success) throw new Error('An unexpected error occured.');

    return { success: true, error: null };
  } catch (error: any | unknown) {
    return axiosErrorMiddleware(error);
  }
};

export const deleteLoader = async ({ service_id, bucket_name }: DeleteLoaderParams) => {
  try {
    const url = `/${service_id}/loaders?bucket_name=${bucket_name}`;
    const response = await loaderClient.delete(url);

    if (response?.data?.error) throw new Error(response?.data?.error);
    if (!response?.data?.success) throw new Error('An unexpected error occured.');

    return { success: true, error: null };
  } catch (error: any | unknown) {
    return axiosErrorMiddleware(error);
  }
};

export const createSlackLoader = async (args: CreateSlackLoaderParams): Promise<CreateSlackLoaderResponse> => {
  const { service_id, bucket_name, channels, region, schedule, secret_name, user_id, org_id } = args;

  try {
    const params = { secret_identifier: secret_name };
    const body = { bucket_name, region, channels, schedule };
    const url = `${INTERNAL_LOADER_URL}/${service_id}/loaders/slack/job`;

    const internalAuthHeaders = {
      Authorization: `Bearer ${getAuthCookie()}`,
      'telnyx-ratelimit-user-id': user_id,
      'telnyx-ratelimit-org-id': org_id,
      'Content-Type': 'application/json',
    };

    const response = await axios.post(url, body, { params, headers: internalAuthHeaders });
    return response.data;
  } catch (error: any | unknown) {
    throw new Error(axiosErrorMiddleware(error).message);
  }
};

export const deleteSlackLoader = async ({ service_id, bucket_name, user_id, org_id }: DeleteLoaderParams) => {
  try {
    const url = `${INTERNAL_LOADER_URL}/${service_id}/loaders/slack/job?bucket_name=${bucket_name}`;
    const internalAuthHeaders = {
      Authorization: `Bearer ${getAuthCookie()}`,
      'telnyx-ratelimit-user-id': user_id,
      'telnyx-ratelimit-org-id': org_id,
      'Content-Type': 'application/json',
    };

    const response = await axios.delete(url, { headers: internalAuthHeaders });
    if (response?.data?.error) throw new Error(response?.data?.error);
    if (!response?.data?.success) throw new Error('An unexpected error occured.');

    return { success: true, error: null };
  } catch (error: any | unknown) {
    throw new Error(axiosErrorMiddleware(error).message);
  }
};
